import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { weekDayRow, type WeeklyReport } from '@adhd/shared';
import { api, type WeekReading } from '../lib/api.js';
import { useAi } from '../lib/ai.js';
import { formatWeek, formatDayShort, summarise } from './History.js';

/**
 * One week, open — and every other week listed under it.
 *
 * WHY IT LEFT THE LOG. It was a <details> fold in a list, and once the
 * grounding check stopped destroying write-ups it turned out to hold six
 * paragraphs of prose, "What happened" and "Threads across the week" — measured
 * at 2,470px on a 375px phone, three full screens. Opening one to read it
 * pushed the day list below the fold and left it there, because <details> does
 * not close itself. His words: "the weekly recap now covers the rest of the log
 * page".
 *
 * FOLDS WERE NEVER THE PROBLEM; WHAT SAT BELOW THEM WAS. That is his
 * observation and it is the whole design. On the Log a recap expands on top of
 * the record it was derived from. Here there is nothing underneath but other
 * weeks, so expanding costs nothing and the cheap skim across weeks is worth
 * having. The Log keeps one row and a door; the reading happens here.
 *
 * NOT A TAB, and not an index screen either. A tab is a mode you can sit on the
 * wrong side of, and the Log has ONE axis — it is a record in time, and a week
 * is a span of that same time, not a different subject.
 *
 * THE MONDAY IS STILL THE URL. /week/2026-08-31 names a week and deep-links to
 * it: that week renders open at the top, the others sit collapsed below. The
 * URL is already the natural key (UNIQUE(user_id, week_start), 001_initial.sql)
 * and it says what it is, which /life-event/4d0678c2… never will.
 *
 * TWO REQUESTS, ON PURPOSE. The named week comes from
 * GET /insights/reports/:weekStart, which is cheap and writes nothing, so the
 * thing he asked for paints without waiting. The list fills in behind it. That
 * list route is also the only caller anywhere that lazily writes a finished
 * week — there is no scheduler — which is why the Log still calls it too and
 * why neither call may be traded for the other.
 */
export function WeekPage(): JSX.Element {
  const { weekStart } = useParams<{ weekStart: string }>();
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [others, setOthers] = useState<WeeklyReport[]>([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!weekStart) return;
    setReport(null);
    setFailed(false);
    void api
      .weekReport(weekStart)
      .then(setReport)
      .catch(() => setFailed(true));
  }, [weekStart]);

  useEffect(() => {
    void api
      .weeklyReports()
      .then(setOthers)
      .catch(() => setOthers([]));
  }, []);

  if (failed) {
    return (
      <div className="stack">
        <Link className="linkish" to="/days">
          ‹ Days
        </Link>
        <div className="banner banner-error">
          There is no write-up for that week. One is written for each week once it has finished.
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '40dvh' }}>
        <span className="spinner" />
      </div>
    );
  }

  const rest = others.filter((r) => r.weekStart !== report.weekStart);

  return (
    <div className="stack">
      <Link className="linkish" to="/days">
        ‹ Days
      </Link>

      <header className="stack-sm stack">
        <h1>{formatWeek(report.weekStart, report.weekEnd)}</h1>
      </header>

      <WeekBody report={report} onChanged={setReport} />

      <WeekReadings weekStart={report.weekStart} />

      {rest.length > 0 && (
        <section className="stack">
          <h2>Other weeks</h2>
          <div className="stack-sm stack">
            {rest.map((r) => (
              <details className="day-summary-fold" key={r.id}>
                {/* Dates alone for a week that was written up — its body
                    carries the figures, and no suffix is itself the signal that
                    there is something in there. The suffix appears only on the
                    weeks worth NOT opening. */}
                <summary>
                  {formatWeek(r.weekStart, r.weekEnd)}
                  {summarise(r, false) && (
                    <span className="muted"> · {summarise(r, false)}</span>
                  )}
                </summary>
                <WeekBody
                  report={r}
                  onChanged={(fresh) =>
                    setOthers((prev) => prev.map((x) => (x.id === fresh.id ? fresh : x)))
                  }
                />
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * One report's contents. ONE rendering, used for the week at the top and for
 * every fold below it — a second copy is how the same concept ends up worded
 * two ways, which this feature has already been repaired for once today.
 */
function WeekBody({
  report,
  onChanged,
}: {
  report: WeeklyReport;
  onChanged: (fresh: WeeklyReport) => void;
}): JSX.Element {
  const { canSummarise } = useAi();
  const [busy, setBusy] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const s = report.stats;
  /* What a narrative could have been written FROM — the server gates on exactly
     this sum (reports.ts), and the copy below reads it rather than inferring it
     from the status. */
  const material = s.checkins + s.thoughtRecords + s.voiceLogs + s.sosEvents;
  const figures = [
    s.checkins > 0 && `${s.checkins} check-in${s.checkins === 1 ? '' : 's'}`,
    s.voiceLogs > 0 && `${s.voiceLogs} entr${s.voiceLogs === 1 ? 'y' : 'ies'}`,
    s.thoughtRecords > 0 && `${s.thoughtRecords} thought record${s.thoughtRecords === 1 ? '' : 's'}`,
    s.sosEvents > 0 && `${s.sosEvents} Help now`,
    s.meanMood !== null && `${s.meanMood > 0 ? '+' : ''}${s.meanMood} average`,
  ].filter(Boolean) as string[];

  return (
    <div className="stack">
      {figures.length > 0 && <p className="muted">{figures.join(' · ')}</p>}

      {/*
        FOUR DIFFERENT THINGS USED TO SAY ONE SENTENCE.

        "Nothing was written for this week." rendered whenever `summaryMd` was
        null, which covers a week nobody touched, a week logged but not written
        in, a model that errored, and a model whose write-up was REFUSED for
        naming people who are not in the entries. The row carries `status` and a
        human-readable `error` and the old component threw both away — so the
        app had already written him a clear explanation and then declined to
        show it.
      */}
      {report.summaryMd === null ? (
        report.status === 'no_data' ? (
          <p className="muted">Nothing was logged that week.</p>
        ) : report.status === 'pending' ? (
          <p className="muted">Still being written.</p>
        ) : report.status === 'failed' ? (
          <div className="stack-sm stack">
            {/* The stored reason, which is usually the good one: a write-up
                naming things absent from the entries is refused rather than
                repaired, and the message says which names did it. */}
            <p className="muted">
              {report.error ?? 'The write-up for this week could not be finished.'}
            </p>
            {canSummarise && (
              <div className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={() => {
                    setBusy(true);
                    setRetryFailed(false);
                    void api
                      .writeWeek(report.weekStart)
                      .then(onChanged)
                      .catch(() => setRetryFailed(true))
                      .finally(() => setBusy(false));
                  }}
                >
                  {busy ? 'Writing…' : 'Try this week again'}
                </button>
                {retryFailed && <span className="faint">That did not work either.</span>}
              </div>
            )}
          </div>
        ) : material === 0 ? (
          /* status 'ready' with no narrative: the anti-fabrication guard did its
             job. READ OFF THE SAME NUMBERS AS THE FIGURES LINE, never asserted
             from the status alone — written the assuming way first, it printed
             "no entries, check-ins or thought records that week" directly
             beneath "10 check-ins · 1 entry · 21 thought records". */
          <p className="muted">
            No entries, check-ins or thought records that week, so there was nothing to write a
            summary from.
          </p>
        ) : (
          <p className="muted">No write-up was made for this week.</p>
        )
      ) : (
        report.summaryMd
          .split(/\n{2,}/)
          .filter((para) => para.trim())
          .map((para, i) => (
            <p className="reading" key={i}>
              {para.replace(/^#+\s*/, '')}
            </p>
          ))
      )}

      {/*
        THE DAY NAMES ARE LINKS, which is the argument for a page rather than a
        fold on the Log turned inside out. Each highlight already carries the day
        it happened on, and the Log already anchors every day as #day-YYYY-MM-DD.
        So a recap stops being a wall standing in front of the days and becomes a
        way into them: read what the week held, tap the day, land on what you
        actually wrote.
      */}
      {report.highlights.length > 0 && (
        <section className="stack-sm stack">
          <h3 className="section-head">What happened</h3>
          {report.highlights.map((h, i) => (
            <p className="faint" key={i} style={{ margin: 0 }}>
              <Link className="run-thread" to={`/history#day-${h.day}`}>
                {formatDayShort(h.day)}
              </Link>{' '}
              — {h.text}
            </p>
          ))}
        </section>
      )}

      {report.patterns.length > 0 && (
        <section className="stack-sm stack">
          <h3 className="section-head">Threads across the week</h3>
          {report.patterns.map((pattern, i) => (
            <p className="faint" key={i} style={{ margin: 0 }}>
              {pattern}
            </p>
          ))}
        </section>
      )}
    </div>
  );
}

/** Why no link shows a written reading, by the share page's own rules (weekShapeForOwner). */
const NOT_ON_LINKS: Record<'no_link' | 'label' | 'current' | 'ongoing', string> = {
  no_link: "No link shows it — no link at this level was live from that week's Monday.",
  label: "No link shows it — it uses a word from a thread's name.",
  current:
    "No link shows it — it uses a word from the name of something still going on, and no link that reads it has Threads.",
  ongoing: 'No link shows it — it may say something was going on, and no link that reads it has Threads.',
};

/** Each reading's heading and the line under it: which links show it, beside what. */
const READING_COPY: Record<WeekReading['kind'], { title: string; note: string }> = {
  seemed: {
    title: 'How the week seemed — on How I seem links',
    note: 'A link at How I seem shows this for the week, beside the seven days and your average mood.',
  },
  held: {
    title: 'What the week held — on What my days held and My worksheets too links',
    note: "A link at either of those levels shows this for the week, beside the seven days and the week's figures.",
  },
};

/**
 * The week's readings for share links, one block for each kind that has one,
 * each with its own switch to leave it off links.
 *
 * The seven day states and the reading, when one was written, and — by the
 * share page's own rules — whether any link actually shows it. Shown at every
 * AI setting — what is already written stays readable — and never written from
 * here; the day-close worker makes each once the week is over, only while a
 * link of its kind is live. Absent for a week that never had one, which is most
 * weeks for anybody without such a link.
 */
function WeekReadings({ weekStart }: { weekStart: string }): JSX.Element | null {
  const [readings, setReadings] = useState<{ seemed: WeekReading | null; held: WeekReading | null } | null>(null);

  useEffect(() => {
    let gone = false;
    void api
      .weekReadings(weekStart)
      .then((r) => {
        if (!gone) setReadings(r);
      })
      .catch(() => {
        if (!gone) setReadings(null);
      });
    return () => {
      gone = true;
    };
  }, [weekStart]);

  if (!readings) return null;
  return (
    <>
      {(['seemed', 'held'] as const).map((kind) => {
        const reading = readings[kind];
        return reading ? (
          <ReadingBlock
            key={kind}
            weekStart={weekStart}
            reading={reading}
            onChanged={(next) => setReadings((prev) => (prev ? { ...prev, [kind]: next } : prev))}
          />
        ) : null;
      })}
    </>
  );
}

function ReadingBlock({
  weekStart,
  reading,
  onChanged,
}: {
  weekStart: string;
  reading: WeekReading;
  onChanged: (next: WeekReading | null) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const copy = READING_COPY[reading.kind];

  return (
    <section className="stack-sm stack">
      <h2>{copy.title}</h2>
      <p className="faint">{copy.note}</p>
      <p className="faint">{weekDayRow(reading.days)}</p>
      {reading.status === 'ready' && reading.reading ? (
        <p className={reading.withdrawn ? 'muted' : 'reading'}>{reading.reading}</p>
      ) : (
        <p className="muted">
          {reading.status === 'thin'
            ? 'Not enough daily readings that week to write one — the link shows the days without it.'
            : reading.status === 'refused'
              ? 'A reading was written and not kept, because it said something the rules for it do not allow. The link shows the days without it.'
              : 'No reading was kept for that week. The link shows the days without it.'}
        </p>
      )}
      {reading.status === 'ready' && !reading.withdrawn && reading.onLinks && reading.onLinks !== 'shown' && (
        <p className="muted">{NOT_ON_LINKS[reading.onLinks]}</p>
      )}
      {reading.status === 'ready' && (
        <label className="option option-row">
          <input
            type="checkbox"
            checked={reading.withdrawn}
            disabled={busy}
            onChange={() => {
              setBusy(true);
              void api
                .setWeekReadingWithdrawn(weekStart, reading.kind, !reading.withdrawn)
                .then((r) => onChanged(r.reading))
                .finally(() => setBusy(false));
            }}
          />
          <span>
            Leave this reading off links
            <span className="faint"> — the days still show.</span>
          </span>
        </label>
      )}
    </section>
  );
}
